// === Web App Theme Context ===
// Same 10 themes as admin portal. Persisted to localStorage + Supabase user_preferences.

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useAuth, getSupabaseClient } from './auth';

interface ThemeDefinition {
  readonly mode: 'dark' | 'light';
  readonly primary: string;
  readonly navBar: string;
  readonly navBarEnd: string;
  readonly secondary: string;
  readonly background: { readonly default: string; readonly paper: string };
}

export interface ThemeOption {
  readonly id: string;
  readonly name: string;
  readonly bg: string;
  readonly paper: string;
  readonly gradient: string;
}

export interface ThemeContextValue {
  readonly currentTheme: string;
  readonly changeTheme: (themeId: string) => Promise<void>;
  readonly themeOptions: readonly ThemeOption[];
  readonly gradientCards: boolean;
  readonly toggleGradientCards: () => void;
  readonly isThemeLoaded: boolean;
}

const THEME_KEY = 'webThemeColor';
const GRADIENT_KEY = 'webGradientCards';

// Coloured themes (plum, violet, red, green, orange, yellow, slate) use near-black
// backgrounds tinted with their hue. Their original mid-tone backgrounds (plum
// #4a3d5c, slate #263238, orange #cc5500...) muted the translucent card and tile
// tints and made the whole app look washed out. The picker swatches in
// themeOptions keep the bright colours so each theme stays recognisable.
const themes: Readonly<Record<string, ThemeDefinition>> = {
  default: { mode: 'dark',   primary: '#1565c0', navBar: '#0a1929', navBarEnd: '#1a3a6e', secondary: '#2e7d32', background: { default: '#0a1929', paper: '#132f4c' } },
  charcoal: { mode: 'dark',   primary: '#2e7d32', navBar: '#000000', navBarEnd: '#1a2a1a', secondary: '#ff8f00', background: { default: '#121212', paper: '#1e1e1e' } },
  dark:    { mode: 'dark',   primary: '#2e7d32', navBar: '#000000', navBarEnd: '#0d1a0d', secondary: '#ff8f00', background: { default: '#000000', paper: '#121212' } },
  plum:    { mode: 'dark',   primary: '#ab47bc', navBar: '#1f1230', navBarEnd: '#5a2d82', secondary: '#2ecc71', background: { default: '#1a1025', paper: '#2a1b3d' } },
  violet:  { mode: 'dark',   primary: '#7e57c2', navBar: '#170f2e', navBarEnd: '#5c35a0', secondary: '#2ecc71', background: { default: '#130d24', paper: '#221a3d' } },
  red:     { mode: 'dark',   primary: '#e53935', navBar: '#240c0c', navBarEnd: '#8b1f1f', secondary: '#2ecc71', background: { default: '#1c0b0b', paper: '#311515' } },
  green:   { mode: 'dark',   primary: '#43a047', navBar: '#0d2214', navBarEnd: '#2e7d32', secondary: '#ffcc02', background: { default: '#0b1a10', paper: '#152c1d' } },
  blue:    { mode: 'dark',   primary: '#90caf9', navBar: '#0a1929', navBarEnd: '#1a3a6e', secondary: '#2e7d32', background: { default: '#0a1929', paper: '#132f4c' } },
  white:   { mode: 'light',  primary: '#2e7d32', navBar: '#ffffff', navBarEnd: '#f0f0f0', secondary: '#1565c0', background: { default: '#f5f5f5', paper: '#ffffff' } },
  orange:  { mode: 'dark',   primary: '#ef6c00', navBar: '#271306', navBarEnd: '#b84d00', secondary: '#2ecc71', background: { default: '#1c1007', paper: '#301d0d' } },
  yellow:  { mode: 'dark',   primary: '#f9a825', navBar: '#211e08', navBarEnd: '#9a8500', secondary: '#2ecc71', background: { default: '#181606', paper: '#2a270c' } },
  slate:   { mode: 'dark',   primary: '#78909c', navBar: '#11191d', navBarEnd: '#3e525c', secondary: '#80cbc4', background: { default: '#0f171b', paper: '#1c272d' } },
} as const;

export const themeOptions: readonly ThemeOption[] = [
  { id: 'default', name: 'Default', bg: '#0a1929', paper: '#132f4c', gradient: 'linear-gradient(135deg, #0a1929 0%, #1565c0 100%)' },
  { id: 'white',   name: 'White',   bg: '#ffffff', paper: '#f5f5f5', gradient: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)' },
  { id: 'charcoal', name: 'Charcoal', bg: '#121212', paper: '#1e1e1e', gradient: 'linear-gradient(135deg, #000000 0%, #1e1e1e 100%)' },
  { id: 'blue',    name: 'Blue',    bg: '#0a1929', paper: '#132f4c', gradient: 'linear-gradient(135deg, #0a1929 0%, #132f4c 100%)' },
  { id: 'dark',    name: 'Dark',    bg: '#000000', paper: '#121212', gradient: 'linear-gradient(135deg, #000000 0%, #212121 100%)' },
  { id: 'slate',   name: 'Slate',   bg: '#263238', paper: '#37474f', gradient: 'linear-gradient(135deg, #1c313a 0%, #455a64 100%)' },
  { id: 'plum',    name: 'Plum',    bg: '#4a3d5c', paper: '#5c4d73', gradient: 'linear-gradient(135deg, #3d2d5c 0%, #7b5ea0 100%)' },
  { id: 'violet',  name: 'Violet',  bg: '#3d2d5c', paper: '#4d3d73', gradient: 'linear-gradient(135deg, #2d1f4a 0%, #5c35a0 100%)' },
  { id: 'red',     name: 'Red',     bg: '#4a1f1f', paper: '#5c2a2a', gradient: 'linear-gradient(135deg, #3d0f0f 0%, #7b2525 100%)' },
  { id: 'green',   name: 'Green',   bg: '#1f4a2d', paper: '#2a5c3a', gradient: 'linear-gradient(135deg, #1a3d24 0%, #2e7d32 100%)' },
  { id: 'orange',  name: 'Orange',  bg: '#cc5500', paper: '#e67300', gradient: 'linear-gradient(135deg, #bf4000 0%, #e65100 100%)' },
  { id: 'yellow',  name: 'Yellow',  bg: '#857800', paper: '#a89500', gradient: 'linear-gradient(135deg, #5d5300 0%, #c8aa00 100%)' },
] as const;

const DEFAULT_DEF: ThemeDefinition = themes['default']!;
const ThemeContext = createContext<ThemeContextValue | null>(null);

export function WebThemeProvider({ children }: { readonly children: ReactNode }) {
  const { user } = useAuth();
  const [currentTheme, setCurrentTheme] = useState<string>(
    () => localStorage.getItem(THEME_KEY) ?? 'default',
  );
  const [gradientCards, setGradientCards] = useState<boolean>(
    () => localStorage.getItem(GRADIENT_KEY) !== 'false',
  );
  const [isThemeLoaded, setIsThemeLoaded] = useState<boolean>(
    () => localStorage.getItem(THEME_KEY) !== null,
  );

  const toggleGradientCards = (): void => {
    setGradientCards((prev) => {
      const next = !prev;
      localStorage.setItem(GRADIENT_KEY, String(next));
      return next;
    });
  };

  // Load from Supabase when user signs in
  // DB sync is silent — localStorage value is shown immediately to avoid flash
  useEffect(() => {
    if (!user) {
      setIsThemeLoaded(true); // Guest mode — use localStorage
      return;
    }

    void (async () => {
      try {
        const supabase = await getSupabaseClient();
        const { data, error } = await supabase
          .from('user_preferences')
          .select('theme_color')
          .eq('id', user.id)
          .maybeSingle();
        if (!error && data?.theme_color) {
          setCurrentTheme(data.theme_color as string);
          localStorage.setItem(THEME_KEY, data.theme_color as string);
        }
      } catch { /* fallback to localStorage */ }
      finally {
        setIsThemeLoaded(true); // Allow render after DB attempt completes
      }
    })();
  }, [user]);

  const changeTheme = async (themeId: string): Promise<void> => {
    setCurrentTheme(themeId);
    localStorage.setItem(THEME_KEY, themeId);
    try {
      if (user) {
        const supabase = await getSupabaseClient();
        await supabase
          .from('user_preferences')
          .upsert({ id: user.id, theme_color: themeId, updated_at: new Date().toISOString() })
          .eq('id', user.id);
      }
    } catch { /* persist already updated locally */ }
  };

  const def: ThemeDefinition = themes[currentTheme] ?? DEFAULT_DEF;

  const muiTheme = createTheme({
    palette: {
      mode: def.mode,
      primary:    { main: def.primary },
      secondary:  { main: def.secondary },
      background: def.background,
    },
    typography: { fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
    },
  });

  return (
    <ThemeContext.Provider value={{ currentTheme, changeTheme, themeOptions, gradientCards, toggleGradientCards, isThemeLoaded }}>
      {isThemeLoaded ? (
        <MuiThemeProvider theme={muiTheme}>
          <GlobalStyles styles={{
            '*::-webkit-scrollbar': { width: 8, height: 8 },
            '*::-webkit-scrollbar-track': { background: def.background.default },
            '*::-webkit-scrollbar-thumb': {
              background: def.background.paper,
              borderRadius: 4,
              border: `2px solid ${def.background.default}`,
            },
            '*::-webkit-scrollbar-thumb:hover': { background: def.primary },
          }} />
          {children}
        </MuiThemeProvider>
      ) : null}
    </ThemeContext.Provider>
  );
}

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used within <WebThemeProvider>');
  return ctx;
}

// Expose navBar colour so AppLayout can apply it without re-reading the full theme
export function getNavBarColor(themeId: string): string {
  return (themes[themeId] ?? DEFAULT_DEF).navBar;
}

// Expose the full nav bar gradient string
export function getNavBarGradient(themeId: string): string {
  const def = themes[themeId] ?? DEFAULT_DEF;
  return `linear-gradient(135deg, ${def.navBar} 0%, ${def.navBarEnd} 100%)`;
}
